import { useState, useEffect } from 'react'
import axios from 'axios';
import jwt_decode from "jwt-decode";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import Loader from "../Loader";
import { useNavigate, Link } from 'react-router-dom';
import formatDateTimeToPST from '../PSTDateConvert';

const InventoryExportHistory = () => {
    const [name, setName] = useState('');
    const [timeZone, setTimeZone] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [role, setRole] = useState('');
    const [access, setAccess] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [exportHistory, setExportHistory] = useState([]);
    const [cid, setCid] = useState('');
    const [inventoryAccess, setInventoryAccess] = useState(false);
    const [userInventoryAccess, setUserInventoryAccess] = useState(true);
    const [isMappingAccessFetched, setIsMappingAccessFetched] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        refreshToken();
        getUserAccess();
    }, []);

    useEffect(() => {
        if (token && cid) {
            getCompanyInventoryAccess();
        }
    }, [token, cid]);

    useEffect(() => {
        if (access.length > 0 && token && isMappingAccessFetched) {
            checkAccess();
        }
    }, [access, token, isMappingAccessFetched, inventoryAccess, userInventoryAccess, role]);

    useEffect(() => {
        if (token) {
            getExportHistory();
        }
    }, [token]);

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/token`);
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setRole(decoded.role);
            setExpire(decoded.exp);
            setTimeZone(decoded.timezone);
            setCid(decoded.cid);
        } catch (error) {
            if (error.response) {
                navigate("/");
                window.location.reload();
            }
        }
    };

    const getUserAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/user-access`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccess(response.data.results);
            setUserInventoryAccess(response.data.user_inventory_switch == 0 ? false : true);
        } catch (error) {
            console.log(error);
        }
    };

    const getCompanyInventoryAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/check-company-e911-access/${cid}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInventoryAccess(response.data.inventory_switch === 1);
        } catch (error) {
            console.log(error);
        } finally {
            setIsMappingAccessFetched(true);
        }
    };

    const hasPermission = (permission) => access.some(item => item.access === permission);

    const checkAccess = () => {
        if (role == 2 && !hasPermission('viewinventory')) {
            navigate('/dashboard');
        } else if (role >= 3 && ((!inventoryAccess) || (inventoryAccess && !userInventoryAccess))) {
            navigate('/dashboard');
        }
    };

    const axiosJWT = axios.create();

    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => Promise.reject(error));

    const getExportHistory = async () => {
        setIsLoading(true);
        try {
            const response = await axiosJWT.get(`${baseUrl}/inventory-export/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setExportHistory(response.data.data || []);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (item) => {
        setPopupOpen(true);
        try {
            const response = await axiosJWT.get(`${baseUrl}/inventory-export/download/${item.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            window.location.assign(response.data.url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download export file.');
        } finally {
            setPopupOpen(false);
        }
    };

    const handleRemove = async (item) => {
        if (item.status_code === 0 || item.status_code === 1) {
            alert('Cannot remove an in-progress export. Please wait for it to complete or fail.');
            return;
        }
        if (!window.confirm('Are you sure you want to remove this export record and file?')) return;
        setPopupOpen(true);
        try {
            await axiosJWT.delete(`${baseUrl}/inventory-export/${item.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await getExportHistory();
        } catch (error) {
            const msg = error?.response?.data?.message || 'Failed to remove export record.';
            alert(msg);
        } finally {
            setPopupOpen(false);
        }
    };

    const renderStatus = (item) => {
        const s = item.status_code;
        if (s === 0 || s === 1) return <span>In Process</span>;
        if (s === 2) return <span>Complete</span>;
        if (s === 3) return <span>Failed</span>;
        return '-';
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {(popupOpen || isLoading) && <Loader />}
                <div className="content-page">
                    <div className="row">
                        <div className="col-8">
                            <div className="left-title">
                                <h1>Inventory Export History</h1>
                            </div>
                        </div>
                        <div className="col-4 top-right-buttons">
                            <div className="export-btn addnew-button">
                                <Link to="/inventory"><span>&larr;</span> Back</Link>
                            </div>
                        </div>
                    </div>

                    <div className="common-white-shadow-background setting-section">
                        <div className="row">
                            <div className="setting-section haft-whitebox">
                                <table className="tablebox search-result-table">
                                    <thead>
                                        <tr>
                                            <th>Created At (UTC)</th>
                                            <th>Requested By</th>
                                            <th>Filters</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exportHistory.length === 0 && (
                                            <tr><td colSpan={5} style={{ textAlign: 'center' }}>No export history found.</td></tr>
                                        )}
                                        {exportHistory.map((item) => (
                                            <tr key={item.id}>
                                                <td>{formatDateTimeToPST(item.created_at, timeZone, role)}</td>
                                                <td>{item.full_name}</td>
                                                <td title={item.filters_label || ''}>{item.filters_label || '-'}</td>
                                                <td>{renderStatus(item)}</td>
                                                <td>
                                                    {item.status_code === 2 && (
                                                        <button
                                                            onClick={() => handleDownload(item)}
                                                            title="Download"
                                                            style={{ marginRight: 8 }}
                                                        >
                                                            <img src={`${process.env.PUBLIC_URL}/images/download.png`} alt="Download" />
                                                        </button>
                                                    )}
                                                    {role <= 2 && (
                                                        <button
                                                            onClick={() => handleRemove(item)}
                                                            title="Remove"
                                                            disabled={item.status_code === 0 || item.status_code === 1}
                                                            style={{ opacity: (item.status_code === 0 || item.status_code === 1) ? 0.4 : 1, cursor: (item.status_code === 0 || item.status_code === 1) ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            <img src={`${process.env.PUBLIC_URL}/images/delete.png`} alt="Remove" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default InventoryExportHistory;
